import React from "react";
import { useQuery } from "@apollo/client";
import { GET_S3_TOP_PERFORMING_ASSETS } from "../../services/graphql/s3-analytics-operations";
import { PieChart } from "../charts/pie-chart";

interface TopPerformingAssetsProps {
  tenantId: string;
  dateRange: {
    from: string;
    to: string;
  };
  limit?: number;
}

interface AssetData {
  assetId: string;
  assetName?: string;
  clicks: number;
  uniqueClicks: number;
  uniqueLinks: number;
  conversionRate?: number;
  firstClick?: string;
  lastClick?: string;
}

// Color palette for the pie chart
const COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#F97316", // Orange
  "#06B6D4", // Cyan
  "#84CC16", // Lime
  "#EC4899", // Pink
  "#6B7280", // Gray
];

export function TopPerformingAssets({
  tenantId,
  dateRange,
  limit = 10,
}: TopPerformingAssetsProps) {
  const { data, loading, error, refetch } = useQuery(
    GET_S3_TOP_PERFORMING_ASSETS,
    {
      variables: {
        tenantId,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        limit,
      },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      skip: !tenantId,
    }
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Performing Assets
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading assets...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("Error loading top performing assets:", error);
    return (
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Performing Assets
          </h2>
        </div>
        <div className="p-6">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-center mb-2">
              Failed to load asset data
            </p>
            <button
              onClick={() => refetch()}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const assets: AssetData[] = data?.getS3TopPerformingAssets || [];

  // Prepare data for pie chart
  const pieChartData = assets.map((asset, index) => ({
    label: asset.assetName || `Asset ${asset.assetId.slice(-8)}`,
    value: asset.clicks,
    color: COLORS[index % COLORS.length],
    percentage: 0, // Will be calculated by the PieChart component
  }));

  // Calculate total clicks for summary
  const totalClicks = assets.reduce((sum, asset) => sum + asset.clicks, 0);
  const totalUniqueClicks = assets.reduce((sum, asset) => sum + asset.uniqueClicks, 0);

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Performing Assets
          </h2>
          <div className="text-sm text-gray-500">
            {assets.length} asset{assets.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {assets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-center">
              No asset data available for the selected date range
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-600">Total Clicks</div>
                <div className="text-2xl font-bold text-blue-900">
                  {totalClicks.toLocaleString()}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm font-medium text-green-600">Unique Clicks</div>
                <div className="text-2xl font-bold text-green-900">
                  {totalUniqueClicks.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Pie Chart */}
            <PieChart
              data={pieChartData}
              size={240}
              showLegend={true}
              showPercentages={true}
              emptyMessage="No asset data available"
            />

            {/* Detailed List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Asset Details
              </h3>
              {assets.slice(0, 5).map((asset, index) => (
                <div
                  key={asset.assetId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {asset.assetName || `Asset ${asset.assetId.slice(-8)}`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {asset.uniqueLinks} unique link{asset.uniqueLinks !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {asset.clicks.toLocaleString()} clicks
                    </div>
                    <div className="text-xs text-gray-500">
                      {asset.uniqueClicks.toLocaleString()} unique
                    </div>
                  </div>
                </div>
              ))}
              
              {assets.length > 5 && (
                <div className="text-center pt-2">
                  <span className="text-sm text-gray-500">
                    +{assets.length - 5} more asset{assets.length - 5 !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
